import React, { useState } from "react";
import { FieldArray } from "formik";
import { useDispatch } from "react-redux";
import { throwError } from "store/slices";
import { dialCode, icons } from "utils/constants";
import { numberOnlyFromInput, convertString } from "utils/helpers";

import Card from "components/Layout/Card";
import Button from "components/form/Button";
import TextInput from "components/form/TextInput";
import Location from "components/form/Location";
import UserDropdown from "components/form/UserDropdown";
import AddCoAuthorPopup from "components/Layout/AddCoAuthorPopup";
import Label from "components/form/Label";

const CoAuthorInformation = ({
    values,
    setFieldValue,
    handleChange,
    errors,
    onBack,
    onNext,
    btnLoading,
}) => {
    const dispatch = useDispatch();
    const { authors_email, author_name, co_authors = [] } = values;
    const [showAddCoAuthorPopup, setShowAddCoAuthorPopup] = useState(false);
    const [typedInvitedCoAuthor, setTypedInvitedCoAuthor] = useState("");

    // Get all used emails for validation
    const getUsedEmails = () => {
        const usedEmails = new Set();

        if (authors_email) {
            usedEmails.add(authors_email.trim().toLowerCase());
        }

        co_authors.forEach(coAuthor => {
            if (coAuthor.email) {
                usedEmails.add(coAuthor.email.trim().toLowerCase());
            }
        });

        return usedEmails;
    };

    // Validate if email is already used
    const isEmailUsed = (email, currentIndex = -1) => {
        if (!email) return false;

        const emailLower = email.trim().toLowerCase();
        const usedEmails = getUsedEmails();

        if (currentIndex >= 0) {
            const currentCoAuthorEmail = co_authors[currentIndex]?.email?.trim().toLowerCase();
            if (currentCoAuthorEmail === emailLower) {
                return false;
            }
        }

        return usedEmails.has(emailLower);
    };

    // Validate co-author email
    const validateCoAuthorEmail = (email, index) => {
        const emailLower = email?.trim()?.toLowerCase();
        if (!emailLower) return false;

        const mainEmail = authors_email?.trim()?.toLowerCase();

        if (emailLower === mainEmail) {
            dispatch(throwError({ message: "Main author cannot be co-author." }));
            setFieldValue(`co_authors[${index}].email`, "");
            return true;
        }

        if (isEmailUsed(emailLower, index)) {
            dispatch(throwError({ message: "This email is already used by another author." }));
            setFieldValue(`co_authors[${index}].email`, "");
            return true;
        }

        return false;
    };
    return (
        <>
            {showAddCoAuthorPopup && (
                <AddCoAuthorPopup
                    id="coAuthor_details"
                    typedData={typedInvitedCoAuthor}
                    existingAuthor={{ name: author_name || "", email: authors_email || "" }}
                    existingList={co_authors.map((c) => ({ name: c.name, email: c.email }))}
                    usedEmails={Array.from(getUsedEmails())}
                    onHide={() => setShowAddCoAuthorPopup(false)}
                    setCoAuthorData={(e) => {
                        if (e?.target?.value?.length > 0) {
                            // let oldData = JSON.parse(JSON.stringify(co_authors));
                            let oldData = co_authors.filter(coAuthor => coAuthor.id !== null);
                            const usedEmails = getUsedEmails();

                            e.target.value.forEach((elm) => {
                                const emailLower = elm?.email_id?.toLowerCase();

                                if (usedEmails.has(emailLower)) {
                                    dispatch(
                                        throwError({
                                            message: `Email ${elm.email_id} is already used and was skipped.`,
                                        })
                                    );
                                    return;
                                }

                                oldData.push({
                                    id: "", // Empty string for invited users
                                    name: elm.name || "",
                                    email: elm.email_id || "",
                                    country: elm.country ? parseInt(elm.country) : "",
                                    phone: elm.whatsapp_number || "",
                                    country_code: elm.country_code || "",
                                });

                                usedEmails.add(emailLower);
                            });

                            setFieldValue("co_authors", oldData);
                        }
                        setShowAddCoAuthorPopup(false);
                    }}
                />
            )}

            <Card className="cp-20 cmt-10">
                <div className="text-16-500 color-1717 cmb-20">Co-Author Information</div>
                <div className="border-bottom cmb-20 color-d1d1"></div>

                <FieldArray
                    name="co_authors"
                    render={(arrayHelpers) => {
                        // Add new co-author
                        const addNewCoAuthor = () => {
                            arrayHelpers.push({
                                id: null, // null for new co-authors (show Dropdown)
                                name: "",
                                email: "",
                                country: "",
                                phone: "",
                                country_code: "",
                            });
                        };

                        return (
                            <>
                                {/* Co-author list */}
                                {co_authors?.length > 0 && (
                                    <div className="co-authors-list">
                                        {co_authors.map((coAuthor, index) => (
                                            <div className="row align-items-start cmb-5" key={index}>
                                                {/* Name - Show TextInput when id is empty string, UserDropdown when id is null or has value */}
                                                <div className="col-md-5 col-lg-3 col-12 cmb-24">
                                                    {coAuthor?.id === "" || coAuthor?.id === undefined ? (
                                                        // Show TextInput for co-authors with empty string id (invited users)
                                                        <TextInput
                                                            IsIcon
                                                            isRequired
                                                            icon={icons?.emailIcon}
                                                            label={`Email ID`}
                                                            id={`co_authors[${index}].email`}
                                                            value={coAuthor.email || ""}
                                                            onChange={(e) => {
                                                                handleChange(e);
                                                                validateCoAuthorEmail(e.target.value, index);
                                                            }}
                                                            error={errors?.co_authors?.[index]?.email}
                                                            placeholder="Enter email"
                                                        />
                                                    ) : (
                                                        // Show UserDropdown for co-authors with null id (new) or actual id (selected)
                                                        <UserDropdown
                                                            isRequired
                                                            showOnSearch
                                                            label={`Email ID`}
                                                            id={`co_authors[${index}].id`}
                                                            placeholder="Search co-author email"
                                                            value={coAuthor.id?.toString() || ""}
                                                            error={errors?.co_authors?.[index]?.email}
                                                            isShowEmail
                                                            onChange={(e) => {
                                                                if (e?.target?.data) {
                                                                    const {
                                                                        id: selectedId,
                                                                        name: selectedName,
                                                                        email_id: selectedEmail,
                                                                        country_id: selectedCountryId,
                                                                        mobile_number: selectedMobileNumber,
                                                                        country_code: selectedCountryCode
                                                                    } = e.target.data;
                                                                    const emailLower = selectedEmail?.toLowerCase();

                                                                    if (isEmailUsed(emailLower, index)) {
                                                                        dispatch(
                                                                            throwError({
                                                                                message: "This email is already used by main author or another co-author.",
                                                                            })
                                                                        );
                                                                        return;
                                                                    }


                                                                    setFieldValue(`co_authors[${index}].id`, selectedId || "");
                                                                    setFieldValue(`co_authors[${index}].name`, selectedName || "");
                                                                    setFieldValue(`co_authors[${index}].email`, selectedEmail || "");
                                                                    setFieldValue(`co_authors[${index}].country`, selectedCountryId ? parseInt(selectedCountryId) : "");
                                                                    setFieldValue(`co_authors[${index}].phone`, selectedMobileNumber || "");
                                                                    setFieldValue(`co_authors[${index}].country_code`, selectedCountryCode || "");
                                                                }
                                                            }}
                                                            existingList={co_authors
                                                                // .filter((_, i) => i !== index)
                                                                .map((c) => ({
                                                                    id: c.id,
                                                                    name: c.name,
                                                                    email_id: c.email,
                                                                    country_id: c.country,
                                                                    mobile_number: c.phone,
                                                                    country_code: c.country_code,
                                                                }))}
                                                            filterID={co_authors
                                                                .filter((_, i) => i !== index)
                                                                .map(c => c.id)
                                                                .filter(id => id && id !== null)}
                                                            handelInvite={(e) => {
                                                                setTypedInvitedCoAuthor(e?.value);
                                                                setShowAddCoAuthorPopup(true);
                                                            }}
                                                        />
                                                    )}
                                                </div>

                                                {/* Email */}
                                                <div className="col-md-4 col-lg-3 col-12 cmb-24">
                                                    <TextInput
                                                        IsIcon
                                                        isRequired
                                                        icon={icons?.userOutline}
                                                        label={`Co-Author ${index + 1}`}
                                                        id={`co_authors[${index}].name`}
                                                        value={coAuthor.name || ""}
                                                        onChange={handleChange}
                                                        error={errors?.co_authors?.[index]?.name}
                                                        placeholder="Enter name"
                                                    />
                                                </div>

                                                {/* Country */}
                                                <div className="col-md-4 col-lg-2 col-12 cmb-24">
                                                    <Location
                                                        isRequired
                                                        type="country"
                                                        data={{
                                                            label: "Country",
                                                            id: `co_authors[${index}].country`,
                                                            value: coAuthor.country || "",
                                                            error: errors?.co_authors?.[index]?.country,
                                                            placeholder: "Select Country",
                                                            onChange: (e) => {
                                                                handleChange(e);
                                                                const selected = dialCode.find(
                                                                    (c) => c?.name === e.target?.data?.country
                                                                );

                                                                if (selected) {
                                                                    setFieldValue(
                                                                        `co_authors[${index}].country_code`,
                                                                        selected.code
                                                                    );
                                                                    setFieldValue(
                                                                        `co_authors[${index}].mobile_no_length`,
                                                                        selected.phoneLength
                                                                    );
                                                                } else {
                                                                    setFieldValue(
                                                                        `co_authors[${index}].country_code`,
                                                                        ""
                                                                    );
                                                                }
                                                            },
                                                        }}
                                                    />
                                                </div>

                                                {/* Contact */}
                                                <div className="col-md-5 col-lg-3 col-12 cmb-24">
                                                    <TextInput
                                                        isRequired
                                                        type="number"
                                                        isPhoneNumber
                                                        label="Contact"
                                                        placeholder="Phone Number"
                                                        id={`co_authors[${index}].phone`}
                                                        value={coAuthor.phone || ""}
                                                        error={errors?.co_authors?.[index]?.phone}
                                                        phoneNumberData={{
                                                            id: `co_authors[${index}].country_code`,
                                                            value: coAuthor.country_code || "",
                                                        }}
                                                        onChange={(e) => {
                                                            if (e.target.id.includes("phone")) {
                                                                handleChange(numberOnlyFromInput(e));
                                                            } else {
                                                                handleChange(e);
                                                                handleChange({
                                                                    target: { id: `co_authors[${index}].phone`, value: "" },
                                                                });
                                                                setFieldValue(
                                                                    `co_authors[${index}].mobile_no_length`,
                                                                    e.target.length
                                                                );
                                                            }
                                                        }}
                                                    />
                                                </div>

                                                {/* Action Buttons */}
                                                <div className='col-md-2 col-lg-1 col-12'>
                                                    <Label />
                                                    <div className='d-flex justify-content-center align-items-center gap-2'>
                                                        <Button
                                                            icon={<i className="bi bi-trash"></i>}
                                                            btnStyle="light-outline"
                                                            className="h-38"
                                                            onClick={() => arrayHelpers.remove(index)}
                                                        />
                                                        {index === co_authors.length - 1 && (
                                                            <Button
                                                                icon={<i className="bi bi-plus fs-5"></i>}
                                                                btnStyle="primary-outline1"
                                                                className="h-38"
                                                                onClick={addNewCoAuthor}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Empty state - Show when no co-authors */}
                                {(!co_authors || co_authors.length === 0) && (
                                    <div className="d-flex">
                                        <Button
                                            id="btn_add_co_author"
                                            text="Add Co-Author"
                                            icon={<span className="me-2"><i className="bi bi-plus-lg"></i></span>}
                                            btnStyle="primary-outline-car"
                                            className="h-38"
                                            onClick={addNewCoAuthor}
                                        />
                                    </div>
                                )}

                                {/* Footer buttons */}
                                {/* <div className="d-flex gap-3 cmt-20">
                                    {onBack && (
                                        <Button
                                            text="Back"
                                            btnStyle="light-outline"
                                            className="cps-40 cpe-40 h-38"
                                            onClick={onBack}
                                            disabled={btnLoading}
                                        />
                                    )}
                                    <Button
                                        text="Proceed Next"
                                        btnStyle="primary-dark"
                                        className="cps-40 cpe-40 h-38"
                                        onClick={onNext}
                                        btnLoading={btnLoading}
                                        disabled={Object.keys(errors).length > 0 || btnLoading}
                                    />
                                </div> */}
                            </>
                        );
                    }}
                />
            </Card>
        </>
    );
};

export default CoAuthorInformation;